import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { EventApiService } from '../../../core/api-services/event-api.service';
import {
  EventAttendanceStatus,
  EventDetail,
  EventRegistrationStatus,
  EventRegistrationSummary,
  EventSummary,
} from '../../../core/models/event';

/**
 * Attendee events page.
 *
 * Lists the events the student is eligible for, lets them sign up (choosing one
 * of the days for a multi-day event) and shows the full detail — including the
 * meeting link — once a registration has been confirmed. On the day they sign
 * themselves in from here.
 */
@Component({
  selector: 'app-student-events',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule],
  templateUrl: './events.html',
  styleUrl: './events.scss',
})
export class StudentEventsComponent implements OnInit {
  private readonly api = inject(EventApiService);

  /** True when rendered inside the student dashboard card, which owns the title. */
  @Input() embedded = false;

  readonly events = signal<EventSummary[]>([]);
  readonly registrations = signal<EventRegistrationSummary[]>([]);
  readonly selected = signal<EventDetail | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly chosenSession = signal<number | null>(null);
  readonly tab = signal<'upcoming' | 'mine' | 'past'>('upcoming');

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getMyEvents()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (events) => this.events.set(events ?? []),
        error: () => this.error.set('Your events could not be loaded.'),
      });

    this.api.getMyRegistrations().subscribe({
      next: (registrations) => this.registrations.set(registrations ?? []),
      error: () => undefined,
    });
  }

  open(event: EventSummary): void {
    this.busy.set(true);
    this.chosenSession.set(null);
    this.api
      .getEvent(event.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.selected.set(detail);
          this.chosenSession.set(detail.mySessionId ?? null);
        },
        error: () => this.error.set('The event could not be opened.'),
      });
  }

  close(): void {
    this.selected.set(null);
  }

  register(): void {
    const event = this.selected();
    if (!event || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .register(event.id, { sessionId: this.chosenSession() })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set(`You are registered for ${event.title}. Confirmation will follow.`);
          this.selected.set(null);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The registration could not be made.'),
      });
  }

  cancel(registration: EventRegistrationSummary): void {
    this.busy.set(true);
    this.error.set('');
    this.api
      .cancelRegistration(registration.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set('Your registration has been withdrawn.');
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The registration could not be withdrawn.'),
      });
  }

  signIn(eventId: number): void {
    this.busy.set(true);
    this.error.set('');
    this.api
      .signIn(eventId)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set('You are signed in to the event.');
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'You could not sign in to the event.'),
      });
  }

  // ---- Helpers -----------------------------------------------------------

  /** The attendee's live registration for an event, if they have one. */
  registrationFor(event: EventSummary): EventRegistrationSummary | undefined {
    return this.registrations().find(
      (registration) =>
        registration.eventId === event.id &&
        registration.status !== 'CANCELLED' &&
        registration.status !== 'REJECTED'
    );
  }

  /** An event has finished once it is closed, called off, or simply in the past. */
  private hasFinished(registration: EventRegistrationSummary): boolean {
    return (
      registration.eventStatus === 'CLOSED' ||
      registration.eventStatus === 'CANCELLED' ||
      new Date(registration.eventStartDate).getTime() < Date.now()
    );
  }

  /** Registrations for events that have not finished yet. */
  activeRegistrations(): EventRegistrationSummary[] {
    return this.registrations().filter((registration) => !this.hasFinished(registration));
  }

  /** Registrations for finished events, newest first — the attendee's history. */
  pastRegistrations(): EventRegistrationSummary[] {
    return this.registrations()
      .filter((registration) => this.hasFinished(registration))
      .sort((a, b) => new Date(b.eventStartDate).getTime() - new Date(a.eventStartDate).getTime());
  }

  /** How the attendee's attendance reads in the history list. */
  attendanceLabel(registration: EventRegistrationSummary): string {
    switch (registration.attendanceStatus) {
      case 'PRESENT':
        return 'Attended';
      case 'ABSENT':
        return 'Did not attend';
      default:
        return 'Not recorded';
    }
  }

  canRegister(event: EventSummary): boolean {
    return event.status === 'PUBLISHED' && !this.registrationFor(event);
  }

  canWithdraw(registration: EventRegistrationSummary): boolean {
    return registration.status === 'PENDING' || registration.status === 'CONFIRMED';
  }

  canSignIn(registration: EventRegistrationSummary): boolean {
    return registration.status === 'CONFIRMED' && registration.attendanceStatus !== 'PRESENT';
  }

  isConfirmed(registration?: EventRegistrationSummary): boolean {
    return registration?.status === 'CONFIRMED';
  }

  statusClass(status: string): string {
    switch (status) {
      case 'PUBLISHED':
        return 'status status-approved';
      case 'CLOSED':
        return 'status status-booked';
      case 'CANCELLED':
        return 'status status-rejected';
      default:
        return 'status status-pending';
    }
  }

  registrationClass(status: EventRegistrationStatus): string {
    switch (status) {
      case 'CONFIRMED':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }

  attendanceClass(status?: EventAttendanceStatus | null): string {
    switch (status) {
      case 'PRESENT':
        return 'status status-approved';
      case 'ABSENT':
        return 'status status-rejected';
      default:
        return 'status status-cancelled';
    }
  }
}
